import moment from "moment";
import Datepicker from "flowbite-datepicker/Datepicker";
import CalendarIcon from "../../assets/calendarIcon.svg";
import { useRef, useState, type ChangeEvent, type FocusEvent } from "react";

interface DatePickerInputProps {
  label: string;
  selectedDate: Date | null;
  onDateChange: (date: Date | null) => void;
  placeholder?: string;
  disableFuture?: boolean;
  disablePast?: boolean;
}

interface CustomDatepicker {
  destroy: () => void;
  show: () => void;
  update: (options: { startDate: string }) => void;
}

const DatePickerInput = ({
  label,
  selectedDate,
  onDateChange,
  placeholder = "YYYY-MM-DD",
  disableFuture = false,
  disablePast = false,
}: DatePickerInputProps) => {
  const dateInputRef = useRef<HTMLInputElement>(null);
  const datepickerRef = useRef<CustomDatepicker | null>(null);
  const [isManualInput, setIsManualInput] = useState(false);

  const initializeDatePicker = () => {
    if (datepickerRef.current || !dateInputRef.current) {
      return;
    }

    const today = moment().format("YYYY-MM-DD");
    const options: any = {
      format: "yyyy-mm-dd",
      autohide: true,
      startView: 2,
    };

    if (disablePast) {
      options.minDate = today;
    }
    if (disableFuture) {
      options.maxDate = today;
    }

    datepickerRef.current = new Datepicker(dateInputRef.current, options);

    dateInputRef.current.addEventListener("changeDate", (event: Event) => {
      const dateValue = (event.target as HTMLInputElement).value;
      onDateChange(dateValue ? moment(dateValue, "YYYY-MM-DD").toDate() : null);
    });
  };

  const handleDateInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    setIsManualInput(true);

    if (datepickerRef.current) {
      datepickerRef.current.destroy();
      datepickerRef.current = null;
    }

    const dateValue = e.target.value;
    const parsedDate = moment(dateValue, "YYYY-MM-DD", true);

    if (parsedDate.isValid()) {
      onDateChange(parsedDate.toDate());
    }
  };

  const handleInputFocus = () => {
    if (isManualInput) {
      setIsManualInput(false);
    } else {
      initializeDatePicker();
      datepickerRef.current?.show();
    }
  };

  const handleInputBlur = (e: FocusEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;

    if (inputValue) {
      const parsedDate = moment(inputValue, "YYYY-MM-DD", true);

      if (parsedDate.isValid()) {
        onDateChange(parsedDate.toDate());
        if (dateInputRef.current) {
          dateInputRef.current.value = parsedDate.format("YYYY-MM-DD");
        }
      } else if (dateInputRef.current) {
        dateInputRef.current.value = selectedDate
          ? moment(selectedDate).format("YYYY-MM-DD")
          : "";
      }
    }
    setIsManualInput(false);
  };

  const handleCalendarIconClick = () => {
    if (dateInputRef.current) {
      dateInputRef.current.focus();
      initializeDatePicker();
      datepickerRef.current?.show();
    }
  };

  return (
    <div className="relative w-full">
      <label
        htmlFor="datePicker"
        className="block mb-2 text-sm font-medium text-gray-900"
      >
        {label}
      </label>
      <div className="relative">
        <img
          src={CalendarIcon}
          alt="Calendar icon"
          className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 cursor-pointer"
          onClick={handleCalendarIconClick}
        />
        <input
          ref={dateInputRef}
          id="datePicker"
          type="text"
          defaultValue={
            selectedDate && moment(selectedDate).isValid()
              ? moment(selectedDate).format("YYYY-MM-DD")
              : ""
          }
          onFocus={handleInputFocus}
          onChange={handleDateInputChange}
          onBlur={handleInputBlur}
          className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-z-dark-moss-green focus:border-z-dark-moss-green block w-full p-2.5 pl-8"
          placeholder={placeholder}
        />
      </div>
    </div>
  );
};

export { DatePickerInput };
